/** Content design tokens: standardized copy patterns, data formats, content limits, and messaging for consistent user-facing content. */

import { ERROR_MESSAGES } from '@federa/const/error-messages';
import { LOADING_MESSAGE_SAVING_SHORT } from '@federa/const/loading-message-values';

/** Loading state messages. `saving` ('Saving changes...') is the standalone status line; `savingShort` ('Saving...') is the in-place pending label a Save button swaps to, where the button verb already supplies 'changes'. Collapsing them silently rewrites the copy on every Save button in the fleet -- keep both and pick by surface.
 *
 * This record has TWO roles: the copy every pending-state surface consumes, AND the runtime-derived forbid corpus
 * `QUAL-INLINE-UI-COPY-LITERAL-001` builds from `Object.entries()` at module load. A member with no callsite is
 * therefore still load-bearing -- it is the alternation branch that stops its text from being re-inlined. The one
 * exception is a member whose VALUE another member already spells: the rule's first-owner-wins guard skips it, so it
 * contributes neither copy nor coverage. `loading` was exactly that (a second 'Loading...' beside `default`) and was
 * reaped; do not reintroduce a duplicate-valued key. */
export const loadingMessages = {
	default: 'Loading...',
	saving: 'Saving changes...',
	/** Bound to the const floor 2026-09-04: two `const/` copy leaves say the same word and may not value-import
	 *  `utils/`, so the string is homed on `LOADING_MESSAGE_SAVING_SHORT` and read here. The VALUE is unchanged, so
	 *  the forbid corpus this record feeds is unchanged. */
	savingShort: LOADING_MESSAGE_SAVING_SHORT,
	deleting: 'Deleting...',
	uploading: 'Uploading file...',
	processing: 'Processing...',
	searching: 'Searching...',
	fetching: 'Fetching data...',
	connecting: 'Connecting...',
	/** The sibling of `connecting` for a socket that HAD a connection and is getting it back. */
	reconnecting: 'Reconnecting...',
	/** What a shell says once auth has INITIALIZED and found nobody signed in -- the sibling of `default`, which is what
	 *  the same spinner says while initialization is still in flight. Both standalone shells that draw that spinner
	 *  (`components/auth/components/StandaloneMissionControlGuard.tsx`, `spa/profile/frontend/main.tsx`) spelled the
	 *  ternary's true arm inline while taking its false arm from `default` here -- one label, half homed. */
	awaitingLogin: 'Please log in to continue...',
} as const;

export const errorMessages = {
	/** ONE decision with two readers, homed on the const floor 2026-09-02: `userSafeError`
	 *  (`utils/errors/error-core.ts`) substitutes this same sentence for any fallback carrying a vendor or
	 *  infrastructure token, and neither `utils/` leaf may value-import the other. */
	generic: ERROR_MESSAGES.GENERIC_RETRY,
	network: 'Unable to connect. Check your internet connection.',
	notFound: 'The requested item could not be found.',
	unauthorized: "You don't have permission to access this.",
	forbidden: 'Access denied.',
	validation: 'Please check your input and try again.',
	timeout: 'Request timed out. Please try again.',
	serverError: 'Server error. Please try again later.',
	rateLimited: 'Too many requests. Please wait and try again.',
	uploadFailed: 'File upload failed. Please try again.',
	invalidFile: 'Invalid file type or size.',
	sessionExpired: 'Your session has expired. Please log in again.',

	// Ledger-specific errors
	ledgerLoad: 'Unable to load ledger transactions.',
	ledgerRefresh: 'Unable to refresh ledger.',
} as const;
